import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Building2, Truck, ClipboardSignature, Clock, Calendar as CalendarIcon } from "lucide-react";
import SignaturePad from "./SignaturePad";
import TimeInput24 from "./TimeInput24";
import { formatDateFr } from "../lib/dateFormat";

const ETATS = [
  { code: "bon", label: "Bon état" },
  { code: "use", label: "Usé" },
  { code: "endommage", label: "Endommagé" },
  { code: "inutilisable", label: "Inutilisable" },
];

/**
 * Widget Dashboard "Enlèvements sites à signer" — visible si le collaborateur
 * connecté a été désigné référent (`contact_collaborateur_id`) sur au moins une
 * demande d'enlèvement encore au statut `pending`. Pattern symétrique à
 * `SiteReceptionsToConfirm.jsx`.
 */
export default function SitePickupsToSign({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/equipment-site-returns/mine-to-pickup");
      setItems(r.data || []);
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onNotif = (ev) => {
      const t = ev?.detail?.type || "";
      if (t.startsWith("equipment_site_return_")) load();
    };
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:equipment-site-returns-updated", load);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:equipment-site-returns-updated", load);
    };
  }, [load]);

  if (loading && items.length === 0) return null;
  if (items.length === 0) return null;

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-sm p-4" data-testid="site-pickups-pending">
      <div className="flex items-center gap-2 mb-3">
        <Truck size={16} className="text-orange-700" />
        <h3 className="text-sm font-display font-semibold text-orange-900">
          À retirer sur site ({items.length})
        </h3>
      </div>
      <p className="text-xs text-orange-800 font-mono mb-3">
        Vous avez été désigné référent pour l&apos;enlèvement du/des matériel(s) ci-dessous. Signez pour confirmer le retrait sur site.
      </p>
      <div className="space-y-2">
        {items.map((r) => (
          <div
            key={r.id}
            className="bg-white border border-orange-200 rounded-sm p-3 flex items-start gap-3 flex-wrap"
            data-testid={`site-pickup-row-${r.id}`}
          >
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm font-semibold text-zinc-900">
                {r.designation_snapshot}{" "}
                <span className="text-zinc-500 font-mono text-xs">({r.ref_id_snapshot || "—"})</span>
              </p>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">
                <Building2 size={10} className="inline mr-1" />
                Site : <b>{r.site_name_snapshot || "—"}</b>
                {" · "}qté <b>{r.quantite_snapshot || 1}</b>
              </p>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">
                <CalendarIcon size={10} className="inline mr-1" />
                Prévu le <b>{r.return_date ? formatDateFr(r.return_date) : "—"}</b>
                {" "}<Clock size={10} className="inline mx-1" />
                <b>{r.return_start_time}</b> → <b>{r.return_end_time}</b>
              </p>
              <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
                Émise par {r.requested_by_name || "—"}
                {r.created_at ? ` le ${formatDateFr(r.created_at.slice(0, 10))}` : ""}
              </p>
              {r.note && (
                <p className="text-[11px] text-zinc-600 mt-0.5 italic">« {r.note} »</p>
              )}
              {r.rejection_count > 0 && r.last_rejection_reason && (
                <p className="text-[11px] text-red-700 font-mono mt-1">
                  Précédemment refusé&nbsp;: {r.last_rejection_reason}
                </p>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => setTarget(r)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid={`site-pickup-sign-${r.id}`}
            >
              <ClipboardSignature size={13} className="mr-1.5" /> Signer l&apos;enlèvement
            </Button>
          </div>
        ))}
      </div>

      {target && (
        <SignPickupDialog
          item={target}
          onClose={() => setTarget(null)}
          onDone={() => {
            setTarget(null);
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function _pad(n) {
  return String(n).padStart(2, "0");
}

function SignPickupDialog({ item, onClose, onDone }) {
  const now = new Date();
  const [actualDate, setActualDate] = useState(
    `${now.getFullYear()}-${_pad(now.getMonth() + 1)}-${_pad(now.getDate())}`,
  );
  const [actualTime, setActualTime] = useState(`${_pad(now.getHours())}:${_pad(now.getMinutes())}`);
  const [etat, setEtat] = useState("bon");
  const [etatNote, setEtatNote] = useState("");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!signature) { toast.error("Signature obligatoire"); return; }
    if (!actualDate || !actualTime) { toast.error("Indiquez la date et l'heure réelles d'enlèvement"); return; }
    try {
      setSubmitting(true);
      await api.put(`/equipment-site-returns/${item.id}/confirm`, {
        actual_date: actualDate,
        actual_time: actualTime,
        etat,
        etat_note: etatNote,
        contact_signature: signature,
      });
      toast.success("Enlèvement signé — en attente de contre-signature.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="site-pickup-sign-dialog"
      >
        <DialogHeader>
          <DialogTitle>Signer l&apos;enlèvement sur site</DialogTitle>
          <DialogDescription>
            Confirmez le retrait du matériel sur site. Un admin / manager devra ensuite contre-signer pour clôturer.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 space-y-1.5">
          <div className="text-xs font-mono text-zinc-700">
            <span className="label-uppercase text-[10px] text-orange-700">Matériel enlevé</span>
            <p className="text-zinc-900 font-semibold mt-0.5">
              {item.designation_snapshot} × {item.quantite_snapshot || 1}
              <span className="text-zinc-500"> ({item.ref_id_snapshot || "—"})</span>
            </p>
          </div>
          <div className="text-xs font-mono text-zinc-700">
            <span className="label-uppercase text-[10px] text-orange-700">Site</span>
            <p className="text-zinc-900 font-semibold mt-0.5">{item.site_name_snapshot || "—"}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Date réelle</Label>
              <Input
                type="date" value={actualDate} onChange={(e) => setActualDate(e.target.value)}
                className="mt-1" data-testid="site-pickup-actual-date"
              />
            </div>
            <div>
              <Label className="label-uppercase">Heure réelle</Label>
              <TimeInput24
                value={actualTime} onChange={setActualTime}
                data-testid="site-pickup-actual-time"
              />
            </div>
          </div>
          <div>
            <Label className="label-uppercase">État du matériel à l&apos;enlèvement</Label>
            <Select value={etat} onValueChange={setEtat}>
              <SelectTrigger className="mt-1" data-testid="site-pickup-etat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ETATS.map((e) => <SelectItem key={e.code} value={e.code}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase">Remarque (optionnel)</Label>
            <Textarea
              value={etatNote} onChange={(e) => setEtatNote(e.target.value)}
              rows={2} className="mt-1"
              placeholder="Précisions sur l'état, défauts constatés…"
              data-testid="site-pickup-etat-note"
            />
          </div>
          <div>
            <Label className="label-uppercase">Votre signature</Label>
            <SignaturePad onSave={setSignature} value={signature} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button
            onClick={submit} disabled={!signature || submitting}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="site-pickup-submit"
          >
            {submitting ? "Envoi…" : "Valider la signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
